import React, { useState, useEffect, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom'; // Unused, as navigate const was removed
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiAlertCircle, 
  FiZap,
  FiEye,
  FiEyeOff,
  FiCalendar,
  FiFileText,
  FiDownload,
  FiEdit,
  FiTrash,
  FiSend
} from 'react-icons/fi';
import Select from 'react-select';
import apiClient from '../apiClient'; // Use the same API client as Projects page
import ConfirmationModal from '../components/DeleteConfirmationModal';
import DocumentSectionSelector from '../components/documents/DocumentSectionSelector';
import SendCommunicationModal from '../components/events/SendCommunicationModal';
import './Plans.css';

// Input validation and sanitization helpers will be added here when needed

const Plans = () => {
  // State management
  const [plans, setPlans] = useState([]);
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);
  const [showSendCommunicationModal, setShowSendCommunicationModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [eventToSend, setEventToSend] = useState(null);
  
  // Event form states
  const [activeTab, setActiveTab] = useState('details');
  const [selectedStakeholders, setSelectedStakeholders] = useState([]);
  const [selectedKeyMessages, setSelectedKeyMessages] = useState([]);
  const [groups, setGroups] = useState([]);
  const [keyMessages, setKeyMessages] = useState([]);
  const [communicationMedium, setCommunicationMedium] = useState('email');
  const [generatedCommunication, setGeneratedCommunication] = useState(null);
  const [isGeneratingCommunication, setIsGeneratingCommunication] = useState(false);
  
  // File upload states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const [eventFiles, setEventFiles] = useState([]);
  
  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'danger',
    onConfirm: () => {},
    isLoading: false
  });

  // New state for visual event system
  const [visibleEvents, setVisibleEvents] = useState({}); // Track which plans have events visible
  
  // Document generation states
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [documentType, setDocumentType] = useState('change_management_plan');
  const [generatedDocuments, setGeneratedDocuments] = useState([]);
  const [showSectionSelector, setShowSectionSelector] = useState(false);
  const [pendingDocumentType, setPendingDocumentType] = useState(null);

  // Helper functions for confirmation modal
  const openConfirmationModal = ({ title, message, confirmText, type = 'danger', onConfirm }) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      confirmText,
      cancelText: 'Cancel',
      type,
      onConfirm,
      isLoading: false
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
  };

  const setConfirmationLoading = (isLoading) => {
    setConfirmationModal(prev => ({ ...prev, isLoading }));
  };

  // Function to open section selector
  const handleGenerateDocument = async (type) => {
    if (!selectedProject) {
      setError('Please select a project first');
      return;
    }
    
    setPendingDocumentType(type);
    setShowSectionSelector(true);
  };
  
  // Function to generate document with selected sections
  const handleGenerateWithSections = async (selectedSections, customSections) => {
    setIsGeneratingDocument(true);
    setDocumentType(pendingDocumentType);
    setError(null);
    
    try {
      const response = await apiClient.post('/documents/generate', {
        project_id: selectedProject,
        document_type: pendingDocumentType,
        selected_sections: selectedSections,
        custom_sections: customSections
      });
      
      if (response.data.document) {
        setSuccessMessage(`${pendingDocumentType === 'change_management_plan' ? 'Change Management Plan' : 'Communication Plan'} generated successfully!`);
        
        // Refresh documents list
        fetchGeneratedDocuments();
        
        // Show the document modal
        setShowDocumentModal(true);
      }
    } catch (err) {
      console.error('Error generating document:', err);
      setError(err.response?.data?.error || 'Failed to generate document');
    } finally {
      setIsGeneratingDocument(false);
      setShowSectionSelector(false);
      setPendingDocumentType(null);
    }
  };
  
  // Function to fetch generated documents
  const fetchGeneratedDocuments = async () => {
    if (!selectedProject) return;
    
    try {
      const response = await apiClient.get(`/documents/project/${selectedProject}`);
      setGeneratedDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };
  
  // Function to download document
  const handleDownloadDocument = async (documentId) => {
    try {
      const response = await apiClient.get(`/documents/${documentId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `document_${documentId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccessMessage('Document downloaded successfully!');
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Failed to download document');
    }
  };

  // Function to delete document
  const handleDeleteDocument = async (documentId) => {
    openConfirmationModal({
      title: 'Delete Document',
      message: 'Are you sure you want to delete this document? This action cannot be undone.',
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          await apiClient.delete(`/documents/${documentId}`);
          setSuccessMessage('Document deleted successfully!');
          fetchGeneratedDocuments(); // Refresh the list
          closeConfirmationModal();
        } catch (err) {
          console.error('Error deleting document:', err);
          setError('Failed to delete document');
          closeConfirmationModal();
        }
      }
    });
  };

  // Function to rename document
  const handleRenameDocument = async (documentId, currentName) => {
    const newName = prompt('Enter new document name:', currentName);
    if (newName && newName !== currentName) {
      try {
        await apiClient.put(`/documents/${documentId}`, {
          document_name: newName
        });
        setSuccessMessage('Document renamed successfully!');
        fetchGeneratedDocuments(); // Refresh the list
      } catch (err) {
        console.error('Error renaming document:', err);
        setError('Failed to rename document');
      }
    }
  };

  // Wrap fetchGroups, fetchKeyMessages, fetchProjects in useCallback as they are in useEffect deps array
  const fetchGroups = useCallback(async () => {
    try {
      const response = await apiClient.get('/stakeholder_groups');
      const data = response.data;
      setGroups(data.map(group => ({
        value: group.id,
        label: group.name
      })));
    } catch (err) {
      console.error('Error fetching stakeholder groups:', err);
    }
  }, []);

  const fetchKeyMessages = useCallback(async () => {
    try {
      const response = await apiClient.get('/key-messages');
      const data = response.data;
      setKeyMessages(data.map(message => ({
        value: message.id,
        label: message.title
      })));
    } catch (err) {
      console.error('Error fetching key messages:', err);
      setError('Failed to load key messages');
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await apiClient.get('/projects/'); // Note the trailing slash
      const data = response.data;
      setProjects(data);
      if (data.length > 0) {
        setSelectedProject(data[0].id);
      }
    } catch (err) {
      setError('Failed to load projects. Please try again.');
      console.error('Error fetching projects:', err);
    }
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([
          fetchProjects(),
          fetchGroups(),
          fetchKeyMessages()
        ]);
      } catch (err) {
        console.error('Error during initialization:', err);
        setError('Failed to initialize Plans page');
      } finally {
        setLoading(false);
      }
    };
    
    initializeData();
  }, [fetchProjects, fetchGroups, fetchKeyMessages]);

  const fetchPlans = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`/projects/${selectedProject}/plans`);
      const data = response.data;
      setPlans(data);
    } catch (err) {
      setError('Failed to load plans. Please try again.');
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedProject) {
      fetchPlans();
      fetchGeneratedDocuments();
    }
  }, [selectedProject, fetchPlans]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreatePlan = () => {
    console.log('🆕 handleCreatePlan called');
    
    // Check if user has any projects
    if (!projects || projects.length === 0) {
      setError('You must have at least one project to create a plan. Please create a project first.');
      return;
    }
    
    setCurrentPlan(null);
    setIsModalOpen(true);
  };

  const handleEditPlan = (plan) => {
    setCurrentPlan(plan);
    setIsModalOpen(true);
  };

  const handleDeletePlan = (planId) => {
    openConfirmationModal({
      title: 'Delete Plan',
      message: 'Are you sure you want to delete this plan? This action cannot be undone.',
      confirmText: 'Delete Plan',
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await apiClient.delete(`/plans/${planId}`);
          fetchPlans();
          closeConfirmationModal();
        } catch (err) {
          setError('Failed to delete plan. Please try again.');
          console.error('Error deleting plan:', err);
          closeConfirmationModal();
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const planData = {
      name: formData.get('name'),
      description: formData.get('description'),
      type: formData.get('type'),
      status: formData.get('status'),
      start_date: formData.get('start_date'),
      end_date: formData.get('end_date'),
      channels: formData.get('channels').split(',').map(c => c.trim()),
      metrics: formData.get('metrics').split(',').map(m => m.trim())
    };

    // Add or update plan
    try {
      setLoading(true);
      if (currentPlan) {
        // Update existing plan
        await apiClient.put(`/plans/${currentPlan.id}`, planData);
        setSuccessMessage('Plan updated successfully!');
      } else {
        // Create new plan
        await apiClient.post(`/projects/${selectedProject}/plans`, planData);
        setSuccessMessage('Plan created successfully!');
      }
      fetchPlans();
      setIsModalOpen(false);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save plan. Please try again.');
      console.error('Error saving plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const response = await apiClient.get(`/events?project_id=${selectedProject}`);
      console.log('📥 Events fetched from server:', response.data);
      setEvents(response.data);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedProject) {
      fetchEvents();
    }
  }, [selectedProject, fetchEvents]);

  const handleCreateEvent = () => {
    setCurrentEvent({
      name: '',
      type: '',
      event_date: new Date().toISOString().slice(0, 16),
      description: '',
      stakeholders: [],
      key_messages: [],
      tone: 'Informative',
      channels: [],
      structured_communications: {
        email: { subject: '', opening: '', body: '', call_to_action: '', closing: '' },
        presentation: { title: '', executive_summary: '', key_points: [], speaker_notes: '', qa_prep: [] },
        workshop: { agenda: [], facilitator_guide: '', participant_brief: '', activities: [] },
        meeting: { agenda: [], talking_points: [], decision_points: [] },
        announcement: { headline: '', summary: '', details: '', next_steps: '' }
      }
    });
    setSelectedStakeholders([]);
    setSelectedKeyMessages([]);
    setEventFiles([]);
    setSelectedFiles([]);
    setIsEventModalOpen(true);
  };

  const handleEditEvent = (event) => {
    console.log('🔍 Editing event - Full event object:', event); // Debug log
    console.log('🔍 Event description:', event.description); // Debug log
    console.log('🔍 Event stakeholder_groups:', event.stakeholder_groups); // Debug log
    
    // Ensure structured_communications exists and has all medium types
    const structured_communications = event.structured_communications || {};
    const completeStructuredComms = {
      email: structured_communications.email || { subject: '', opening: '', body: '', call_to_action: '', closing: '' },
      presentation: structured_communications.presentation || { title: '', executive_summary: '', key_points: [], speaker_notes: '', qa_prep: [] },
      workshop: structured_communications.workshop || { agenda: [], facilitator_guide: '', participant_brief: '', activities: [] },
      meeting: structured_communications.meeting || { agenda: [], talking_points: [], decision_points: [] },
      announcement: structured_communications.announcement || { headline: '', summary: '', details: '', next_steps: '' }
    };

    // Parse any JSON string fields for arrays
    Object.keys(completeStructuredComms).forEach(medium => {
      const mediumData = completeStructuredComms[medium];
      Object.keys(mediumData).forEach(field => {
        // Handle array fields that might be JSON strings
        if (typeof mediumData[field] === 'string' && 
            (field === 'key_points' || field === 'qa_prep' || field === 'agenda' || 
             field === 'activities' || field === 'talking_points' || field === 'decision_points')) {
          try {
            mediumData[field] = JSON.parse(mediumData[field]);
          } catch (e) {
            // If parsing fails, ensure it's an array
            mediumData[field] = Array.isArray(mediumData[field]) ? mediumData[field] : [];
          }
        }
        
        // Handle announcement fields that might be objects
        if (medium === 'announcement' && typeof mediumData[field] === 'object' && mediumData[field] !== null) {
          if (field === 'headline' || field === 'summary' || field === 'details' || field === 'next_steps') {
            console.log(`Processing announcement field: ${field}`, mediumData[field]); // Debug log
            // Convert object to string if it's an object
            if (typeof mediumData[field] === 'object') {
              mediumData[field] = typeof mediumData[field] === 'string' 
                ? mediumData[field] 
                : JSON.stringify(mediumData[field]);
            }
          }
        }
        
        // Convert structured objects to readable strings for display
        if (Array.isArray(mediumData[field])) {
          mediumData[field] = mediumData[field].map(item => {
            if (typeof item === 'object' && item !== null) {
              // Handle structured agenda items like {time: "9:00 AM", item: "Welcome"}
              if (item.time && item.item) {
                return `${item.time} - ${item.item}`;
              }
              // Handle structured activity items like {activity: "Brainstorming", description: "..."}
              if (item.activity && item.description) {
                return `${item.activity}: ${item.description}`;
              }
              // Handle other structured objects by converting key-value pairs to readable text
              if (typeof item === 'object') {
                return Object.entries(item)
                  .filter(([key, value]) => value && value.toString().trim() !== '')
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(' | ');
              }
            }
            return typeof item === 'string' ? item : String(item || '');
          });
        }
        
        // Ensure all announcement fields are strings
        if (medium === 'announcement') {
          if (typeof mediumData[field] !== 'string') {
            mediumData[field] = String(mediumData[field] || '');
          }
        }
      });
    });

    // Prepare stakeholder and key message selections for the form
    const stakeholderSelections = event.stakeholder_groups?.map(group => group.id) || [];
    const keyMessageSelections = event.key_messages?.map(message => message.id) || [];

    console.log('Stakeholder groups from event:', event.stakeholder_groups); // Debug log
    console.log('Key messages from event:', event.key_messages); // Debug log

    // Map backend fields to frontend form fields
    setCurrentEvent({
      id: event.id,
      name: event.name || '',
      type: event.event_type || event.type || '', // Map event_type to type for the form
      event_date: event.event_date || '',
      description: event.description || '',
      stakeholders: stakeholderSelections,
      key_messages: keyMessageSelections,
      structured_communications: completeStructuredComms,
      tone: event.preferred_tone || 'Informative',
      channels: event.channels || [],
      target_medium: event.target_medium || 'email',
      generated_communication: event.generated_communication || null
    });

    setSelectedStakeholders(stakeholderSelections.map(id => {
      const group = groups.find(g => g.value === id);
      return group || { value: id, label: `Group ${id}` };
    }));

    setSelectedKeyMessages(keyMessageSelections.map(id => {
      const message = keyMessages.find(m => m.value === id);
      return message || { value: id, label: `Message ${id}` };
    }));

    // Load event files
    setEventFiles(event.documents || []);
    setSelectedFiles([]);
    
    // Set communication medium based on event's target_medium if available
    if (event.target_medium) {
      setCommunicationMedium(event.target_medium);
    } else {
      setCommunicationMedium('email'); // Default to email if no target medium
    }

    setIsEventModalOpen(true);
  };

  const handleDeleteEvent = (eventId) => {
    openConfirmationModal({
      title: 'Delete Event',
      message: 'Are you sure you want to delete this event? This action cannot be undone.',
      confirmText: 'Delete Event',
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await apiClient.delete(`/events/${eventId}`);
          fetchEvents();
          closeConfirmationModal();
          setSuccessMessage('Event deleted successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
          setError('Failed to delete event. Please try again.');
          console.error('Error deleting event:', err);
          closeConfirmationModal();
        }
      }
    });
  };

  const handleSendCommunicationSuccess = (result) => {
    setShowSendCommunicationModal(false);
    setEventToSend(null);
    setSuccessMessage(`Successfully created ${result.communications_created} communications for ${result.stakeholders_count} stakeholders!`);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleGenerateCommunication = async (event, medium = 'email') => {
    try {
      console.log('🚀 Starting communication generation for:', { eventId: event.id, medium });
      setIsGeneratingCommunication(true);
      setError(null); // Clear any previous errors
      
      // Prepare request data with explicit timeout
      const requestData = {
        medium: medium, // email, presentation, workshop, meeting, announcement
        event_type: event.type,
        event_name: event.name,
        event_description: event.description,
        event_date: event.event_date,
        stakeholders: event.stakeholders,
        key_messages: event.key_messages,
        tone: event.tone,
        channels: event.channels,
        purpose: 'inform',
        action_required: event.action_required || '',
        support_resources: event.support_resources || [],
        additional_notes: event.additional_notes || '',
        estimated_duration: event.estimated_duration || '',
        preparation_required: event.preparation_required || '',
        follow_up_actions: event.follow_up_actions || []
      };
      
      console.log('📤 Sending request with data:', requestData);
      
      // Make API call with extended timeout for AI generation
      const response = await apiClient.post(`/events/${event.id}/generate-communication`, requestData, {
        timeout: 60000, // 60 second timeout for AI generation
        validateStatus: function (status) {
          return status < 500; // Resolve only if the status code is less than 500
        }
      });
      
      console.log('📥 Received response:', response);
      
      // Check if response was successful
      if (response.status >= 400) {
        throw new Error(response.data?.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const { communication, event: updatedEventData } = response.data;
      
      console.log('🎯 AI Communication Generated:', communication);
      console.log('🎯 Medium:', medium);
      console.log('🎯 Updated event data:', updatedEventData);
      
      // IMPORTANT: Set communication data immediately for modal display
      setGeneratedCommunication(communication);
      setCommunicationMedium(medium);
      
      // Process and update currentEvent with the new structured communications
      const structured_communications = updatedEventData.structured_communications || {};
      const completeStructuredComms = {
        email: structured_communications.email || { subject: '', opening: '', body: '', call_to_action: '', closing: '' },
        presentation: structured_communications.presentation || { title: '', executive_summary: '', key_points: [], speaker_notes: '', qa_prep: [] },
        workshop: structured_communications.workshop || { agenda: [], facilitator_guide: '', participant_brief: '', activities: [] },
        meeting: structured_communications.meeting || { agenda: [], talking_points: [], decision_points: [] },
        announcement: structured_communications.announcement || { headline: '', summary: '', details: '', next_steps: '' }
      };

      // Update currentEvent state with new data
      setCurrentEvent(prev => ({
        ...prev,
        id: updatedEventData.id,
        name: updatedEventData.name || prev.name,
        type: updatedEventData.event_type || prev.type,
        event_date: updatedEventData.event_date || prev.event_date,
        description: updatedEventData.description || prev.description,
        structured_communications: completeStructuredComms,
        target_medium: updatedEventData.target_medium || medium,
        generated_communication: updatedEventData.generated_communication || communication
      }));
      
      // CRITICAL: Open the modal AFTER setting all the data
      console.log('🎭 Opening communication modal with data:', { communication, medium });
      setShowCommunicationModal(true);
      
      // Show success message
      setSuccessMessage(`✅ AI ${medium} content generated successfully! Review it in the modal above, then click Update Event to save.`);
      setTimeout(() => setSuccessMessage(null), 8000); // Longer timeout to let user read
      
    } catch (err) {
      console.error('❌ Error generating communication:', err);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      
      // Provide specific error messages based on error type
      let errorMessage = 'Failed to generate communication. Please try again.';
      
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        errorMessage = 'AI generation timed out. This can happen with complex requests. Please try again.';
      } else if (err.response?.status === 500) {
        if (err.response?.data?.error?.includes('OpenAI') || err.response?.data?.error?.includes('API key')) {
          errorMessage = 'AI service is not properly configured. Please contact your administrator.';
        } else if (err.response?.data?.error?.includes('rate limit')) {
          errorMessage = 'AI service rate limit exceeded. Please wait a few minutes and try again.';
        } else {
          errorMessage = `Server error: ${err.response?.data?.error || err.response?.data?.details || 'Unknown server error'}`;
        }
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to generate communications for this event.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Event not found. Please refresh the page and try again.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = `Network error: ${err.message}`;
      }
      
      setError(errorMessage);
      
      // Auto-clear error after 10 seconds
      setTimeout(() => setError(null), 10000);
      
    } finally {
      setIsGeneratingCommunication(false);
    }
  };

  const saveEventToBackend = async (eventData, eventId = null) => {
    const payload = {
      name: eventData.name,
      event_type: eventData.type,
      event_date: eventData.event_date,
      importance_level: 'Medium',
      preferred_tone: eventData.tone,
      project_id: selectedProject,
      key_message_ids: eventData.key_messages,
      stakeholder_group_ids: eventData.stakeholders,
      description: eventData.description,
      channels: eventData.channels,
      generated_communication: eventData.generated_communication,
      // Include structured communication fields
      target_medium: eventData.target_medium,
      communication_purpose: eventData.communication_purpose
    };

    console.log('🔧 Payload stakeholder_group_ids:', payload.stakeholder_group_ids);
    console.log('🔧 eventData.stakeholders:', eventData.stakeholders);

    // Add individual structured communication fields based on the structured_communications object
    if (eventData.structured_communications) {
      const structComms = eventData.structured_communications;
      
      // Email fields
      if (structComms.email) {
        payload.email_subject = typeof structComms.email.subject === 'string' 
          ? structComms.email.subject 
          : JSON.stringify(structComms.email.subject || '');
        payload.email_opening = typeof structComms.email.opening === 'string' 
          ? structComms.email.opening 
          : JSON.stringify(structComms.email.opening || '');
        payload.email_body = typeof structComms.email.body === 'string' 
          ? structComms.email.body 
          : JSON.stringify(structComms.email.body || '');
        payload.email_call_to_action = typeof structComms.email.call_to_action === 'string' 
          ? structComms.email.call_to_action 
          : JSON.stringify(structComms.email.call_to_action || '');
        payload.email_closing = typeof structComms.email.closing === 'string' 
          ? structComms.email.closing 
          : JSON.stringify(structComms.email.closing || '');
      }
      
      // Presentation fields
      if (structComms.presentation) {
        payload.presentation_title = typeof structComms.presentation.title === 'string' 
          ? structComms.presentation.title 
          : JSON.stringify(structComms.presentation.title || '');
        payload.presentation_executive_summary = typeof structComms.presentation.executive_summary === 'string' 
          ? structComms.presentation.executive_summary 
          : JSON.stringify(structComms.presentation.executive_summary || '');
        payload.presentation_key_points = Array.isArray(structComms.presentation.key_points) 
          ? structComms.presentation.key_points 
          : JSON.parse(structComms.presentation.key_points || '[]');
        payload.presentation_speaker_notes = typeof structComms.presentation.speaker_notes === 'string' 
          ? structComms.presentation.speaker_notes 
          : JSON.stringify(structComms.presentation.speaker_notes || '');
        payload.presentation_qa_prep = Array.isArray(structComms.presentation.qa_prep) 
          ? structComms.presentation.qa_prep 
          : JSON.parse(structComms.presentation.qa_prep || '[]');
      }
      
      // Workshop fields
      if (structComms.workshop) {
        payload.workshop_agenda = Array.isArray(structComms.workshop.agenda) 
          ? structComms.workshop.agenda 
          : JSON.parse(structComms.workshop.agenda || '[]');
        payload.workshop_facilitator_guide = typeof structComms.workshop.facilitator_guide === 'string' 
          ? structComms.workshop.facilitator_guide 
          : JSON.stringify(structComms.workshop.facilitator_guide || '');
        payload.workshop_participant_brief = typeof structComms.workshop.participant_brief === 'string' 
          ? structComms.workshop.participant_brief 
          : JSON.stringify(structComms.workshop.participant_brief || '');
        payload.workshop_activities = Array.isArray(structComms.workshop.activities) 
          ? structComms.workshop.activities 
          : JSON.parse(structComms.workshop.activities || '[]');
      }
      
      // Meeting fields
      if (structComms.meeting) {
        payload.meeting_agenda = Array.isArray(structComms.meeting.agenda) 
          ? structComms.meeting.agenda 
          : JSON.parse(structComms.meeting.agenda || '[]');
        payload.meeting_talking_points = Array.isArray(structComms.meeting.talking_points) 
          ? structComms.meeting.talking_points 
          : JSON.parse(structComms.meeting.talking_points || '[]');
        payload.meeting_decision_points = Array.isArray(structComms.meeting.decision_points) 
          ? structComms.meeting.decision_points 
          : JSON.parse(structComms.meeting.decision_points || '[]');
      }
      
      // Announcement fields
      if (structComms.announcement) {
        payload.announcement_headline = typeof structComms.announcement.headline === 'string' 
          ? structComms.announcement.headline 
          : JSON.stringify(structComms.announcement.headline || '');
        payload.announcement_summary = typeof structComms.announcement.summary === 'string' 
          ? structComms.announcement.summary 
          : JSON.stringify(structComms.announcement.summary || '');
        payload.announcement_details = typeof structComms.announcement.details === 'string' 
          ? structComms.announcement.details 
          : JSON.stringify(structComms.announcement.details || '');
        payload.announcement_next_steps = typeof structComms.announcement.next_steps === 'string' 
          ? structComms.announcement.next_steps 
          : JSON.stringify(structComms.announcement.next_steps || '');
      }
    }

    try {
      if (eventId) {
        // Update existing event
        console.log('🔧 Updating event with payload:', payload);
        await apiClient.put(`/events/${eventId}`, payload);
        setSuccessMessage('Event updated successfully!');
      } else {
        // Create new event
        console.log('🔧 Creating new event with payload:', payload);
        await apiClient.post('/events', payload);
        setSuccessMessage('Event created successfully!');
      }
      
      // Upload files if any are selected
      if (selectedFiles.length > 0 && eventId) {
        await uploadFiles(eventId);
      }
      
      fetchEvents();
      setIsEventModalOpen(false);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving event:', err);
      console.error('Payload sent:', payload);
      console.error('Event data:', eventData);
      
      // Provide more specific error messages
      if (err.response?.status === 400) {
        setError(`Validation error: ${err.response?.data?.error || 'Please check your input data.'}`);
      } else if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to save this event.');
      } else if (err.response?.status === 404) {
        setError('Event or project not found. Please refresh the page.');
      } else if (err.response?.status === 500) {
        setError(`Server error: ${err.response?.data?.error || 'Internal server error occurred.'}`);
      } else if (err.response?.data?.error) {
        setError(`Error: ${err.response.data.error}`);
      } else if (err.message) {
        setError(`Network error: ${err.message}`);
      } else {
        setError('Failed to save event. Please try again.');
      }
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // Basic event data
    const eventData = {
      name: formData.get('name'),
      type: formData.get('type'),
      event_date: formData.get('event_date'),
      description: formData.get('description'),
      stakeholders: currentEvent.stakeholders,
      key_messages: currentEvent.key_messages,
      tone: formData.get('tone'),
      channels: formData.get('channels')?.split(',').map(c => c.trim()) || [],
      target_medium: communicationMedium,
      structured_communications: currentEvent.structured_communications,
      generated_communication: currentEvent.generated_communication
    };
    
    // Debug logging for troubleshooting
    console.log('🔍 Event Submit Debug:');
    console.log('- Description from form:', formData.get('description'));
    console.log('- Description from currentEvent:', currentEvent.description);
    console.log('- Stakeholders from currentEvent:', currentEvent.stakeholders);
    console.log('- Full eventData being sent:', eventData);
    console.log('- Current event state:', currentEvent);
    
    // Debug logging for troubleshooting
    console.log('🔍 Event Submit Debug:');
    console.log('- Description from form:', formData.get('description'));
    console.log('- Description from currentEvent:', currentEvent.description);
    console.log('- Stakeholders from currentEvent:', currentEvent.stakeholders);
    console.log('- Full eventData being sent:', eventData);
    console.log('- Current event state:', currentEvent);
    
    try {
      setLoading(true);
      await saveEventToBackend(eventData, currentEvent.id);
    } catch (err) {
      console.error('Error saving event:', err);
      console.error('Event data:', eventData);
      
      // Provide more specific error messages
      if (err.response?.status === 400) {
        setError(`Validation error: ${err.response?.data?.error || 'Please check your input data.'}`);
      } else if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to save this event.');
      } else if (err.response?.status === 404) {
        setError('Event or project not found. Please refresh the page.');
      } else if (err.response?.status === 500) {
        setError(`Server error: ${err.response?.data?.error || 'Internal server error occurred.'}`);
      } else if (err.response?.data?.error) {
        setError(`Error: ${err.response.data.error}`);
      } else if (err.message) {
        setError(`Network error: ${err.message}`);
      } else {
        setError('Failed to save event. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const validTypes = [
        'application/pdf', 
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
        'application/vnd.ms-excel',  // Excel .xls files
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',  // Excel .xlsx files
        'text/plain', 
        'image/jpeg', 
        'image/png'
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        setFileError(`Invalid file type: ${file.name}. Only PDF, DOC, DOCX, XLS, XLSX, TXT, JPG, and PNG files are allowed.`);
        return false;
      }
      
      if (file.size > maxSize) {
        setFileError(`File too large: ${file.name}. Maximum file size is 5MB.`);
        return false;
      }
      
      return true;
    });
    
    if (validFiles.length > 0) {
      setSelectedFiles(prevFiles => [...prevFiles, ...validFiles]);
      setFileError('');
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    
    const files = Array.from(e.dataTransfer.files);
    
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const validTypes = [
        'application/pdf', 
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
        'application/vnd.ms-excel',  // Excel .xls files
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',  // Excel .xlsx files
        'text/plain', 
        'image/jpeg', 
        'image/png'
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        setFileError(`Invalid file type: ${file.name}. Only PDF, DOC, DOCX, XLS, XLSX, TXT, JPG, and PNG files are allowed.`);
        return false;
      }
      
      if (file.size > maxSize) {
        setFileError(`File too large: ${file.name}. Maximum file size is 5MB.`);
        return false;
      }
      
      return true;
    });
    
    if (validFiles.length > 0) {
      setSelectedFiles(prevFiles => [...prevFiles, ...validFiles]);
      setFileError('');
    }
  };

  const uploadFiles = async (eventId) => {
    if (selectedFiles.length === 0) return;
    
    setUploadStatus('Uploading files...');
    
    const uploadPromises = selectedFiles.map(async (file, index) => {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await apiClient.post(`/events/${eventId}/documents`, formData, {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({
              ...prev,
              [index]: percentCompleted
            }));
          }
        });
        
        return response.data;
      } catch (err) {
        console.error(`Error uploading file ${file.name}:`, err);
        throw err;
      }
    });
    
    try {
      await Promise.all(uploadPromises);
      setUploadStatus('All files uploaded successfully!');
      setSelectedFiles([]);
      setUploadProgress({});
    } catch (err) {
      setUploadStatus('Error uploading one or more files. Please try again.');
    } finally {
      // All uploading state is managed by uploadStatus and uploadProgress now
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleDeleteEventDocument = (eventId, documentId) => {
    openConfirmationModal({
      title: 'Delete Document',
      message: 'Are you sure you want to delete this document? This action cannot be undone.',
      confirmText: 'Delete Document',
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await apiClient.delete(`/events/${eventId}/documents/${documentId}`);
          // Update event files list
          setEventFiles(prevFiles => prevFiles.filter(file => file.id !== documentId));
          closeConfirmationModal();
          setSuccessMessage('Document deleted successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
          setError('Failed to delete document. Please try again.');
          console.error('Error deleting document:', err);
          closeConfirmationModal();
        }
      }
    });
  };

  // Toggle event visibility for visual event system
  const toggleEventVisibility = (planId) => {
    setVisibleEvents(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const handleStakeholderChange = (selectedOptions) => {
    setSelectedStakeholders(selectedOptions || []);
    setCurrentEvent(prev => ({
      ...prev,
      stakeholders: selectedOptions ? selectedOptions.map(option => option.value) : []
    }));
  };

  const handleKeyMessageChange = (selectedOptions) => {
    setSelectedKeyMessages(selectedOptions || []);
    setCurrentEvent(prev => ({
      ...prev,
      key_messages: selectedOptions ? selectedOptions.map(option => option.value) : []
    }));
  };

  const handleStructuredCommunicationChange = (medium, field, value) => {
    setCurrentEvent(prev => ({
      ...prev,
      structured_communications: {
        ...prev.structured_communications,
        [medium]: {
          ...prev.structured_communications[medium],
          [field]: value
        }
      }
    }));
  };

  const addArrayItem = (medium, field) => {
    setCurrentEvent(prev => {
      const currentArray = prev.structured_communications[medium][field] || [];
      return {
        ...prev,
        structured_communications: {
          ...prev.structured_communications,
          [medium]: {
            ...prev.structured_communications[medium],
            [field]: [...currentArray, '']
          }
        }
      };
    });
  };

  const updateArrayItem = (medium, field, index, value) => {
    setCurrentEvent(prev => {
      const currentArray = [...prev.structured_communications[medium][field]];
      currentArray[index] = value;
      return {
        ...prev,
        structured_communications: {
          ...prev.structured_communications,
          [medium]: {
            ...prev.structured_communications[medium],
            [field]: currentArray
          }
        }
      };
    });
  };

  const removeArrayItem = (medium, field, index) => {
    setCurrentEvent(prev => {
      const currentArray = [...prev.structured_communications[medium][field]];
      currentArray.splice(index, 1);
      return {
        ...prev,
        structured_communications: {
          ...prev.structured_communications,
          [medium]: {
            ...prev.structured_communications[medium],
            [field]: currentArray
          }
        }
      };
    });
  };

  // Render functions for UI components
  const renderPlanModal = () => {
    console.log('🎭 renderPlanModal called - isModalOpen:', isModalOpen, 'currentPlan:', currentPlan);
    
    if (!isModalOpen) {
      console.log('🚫 Modal not open, returning null');
      return null;
    }
    
    console.log('✅ Rendering modal');
    
    return (
      <div className="modal show">
        <div className="modal-backdrop" onClick={() => {
          console.log('🖱️ Backdrop clicked, closing modal');
          setIsModalOpen(false);
        }}></div>
        <div className="modal-content">
          <div className="modal-header">
            <h2>{currentPlan ? 'Edit Plan' : 'Create New Plan'}</h2>
            <button className="close-button" onClick={() => {
              console.log('❌ Close button clicked, closing modal');
              setIsModalOpen(false);
            }}>&times;</button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="name">Plan Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  defaultValue={currentPlan?.name || ''}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  defaultValue={currentPlan?.description || ''}
                  rows="3"
                ></textarea>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="type">Plan Type</label>
                  <select
                    id="type"
                    name="type"
                    defaultValue={currentPlan?.type || 'communication'}
                    required
                  >
                    <option value="communication">Communication Plan</option>
                    <option value="engagement">Engagement Plan</option>
                    <option value="training">Training Plan</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    defaultValue={currentPlan?.status || 'draft'}
                    required
                  >
                    <option value="draft">Draft</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="start_date">Start Date</label>
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    defaultValue={currentPlan?.start_date ? currentPlan.start_date.slice(0, 10) : ''}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="end_date">End Date</label>
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    defaultValue={currentPlan?.end_date ? currentPlan.end_date.slice(0, 10) : ''}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="channels">Communication Channels (comma-separated)</label>
                <input
                  type="text"
                  id="channels"
                  name="channels"
                  defaultValue={currentPlan?.channels ? currentPlan.channels.join(', ') : 'Email, Teams, Intranet'}
                />
              </div>
              <div className="form-group">
                <label htmlFor="metrics">Success Metrics (comma-separated)</label>
                <input
                  type="text"
                  id="metrics"
                  name="metrics"
                  defaultValue={currentPlan?.metrics ? currentPlan.metrics.join(', ') : 'Awareness, Engagement, Adoption'}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="button secondary" onClick={() => {
                console.log('🚫 Cancel button clicked, closing modal');
                setIsModalOpen(false);
              }}>Cancel</button>
              <button type="submit" className="button primary" disabled={loading}>
                {loading ? 'Saving...' : currentPlan ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderEventModal = () => {
    if (!currentEvent || !isEventModalOpen) return null;

    return (
      <div className="modal event-modal show">
        <div className="modal-backdrop" onClick={() => {
          console.log('🖱️ Event modal backdrop clicked, closing modal');
          setIsEventModalOpen(false);
        }}></div>
        <div className="modal-content">
          <div className="modal-header">
            <h2>{currentEvent.id ? 'Edit Event' : 'Create New Event'}</h2>
            <button className="close-button" onClick={() => {
              console.log('❌ Event modal Close button clicked, closing modal');
              setIsEventModalOpen(false);
            }}>&times;</button>
          </div>
          <form onSubmit={handleEventSubmit}>
            <div className="modal-body">
              <div className="form-tabs">
                <button 
                  type="button" 
                  className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
                  onClick={() => setActiveTab('details')}
                >
                  Event Details
                </button>
                <button 
                  type="button" 
                  className={`tab-button ${activeTab === 'communication' ? 'active' : ''}`}
                  onClick={() => setActiveTab('communication')}
                >
                  Communication Content
                </button>
                <button 
                  type="button" 
                  className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}
                  onClick={() => setActiveTab('documents')}
                >
                  Documents
                </button>
              </div>

              {/* Tab Content Container */}
              <div className="tab-content-container">
                {/* Event Details Tab */}
                <div className={`tab-content ${activeTab === 'details' ? 'active' : ''}`}>
                <div className="form-group">
                  <label htmlFor="name">Event Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={currentEvent.name || ''}
                    onChange={(e) => setCurrentEvent({...currentEvent, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="type">Event Type</label>
                    <select
                      id="type"
                      name="type"
                      value={currentEvent.type || ''}
                      onChange={(e) => setCurrentEvent({...currentEvent, type: e.target.value})}
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="Town Hall">Town Hall</option>
                      <option value="Training Launch">Training Launch</option>
                      <option value="Go-Live Notification">Go-Live Notification</option>
                      <option value="Team Meeting">Team Meeting</option>
                      <option value="Workshop">Workshop</option>
                      <option value="Email Communication">Email Communication</option>
                      <option value="Leadership Briefing">Leadership Briefing</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="event_date">Date & Time</label>
                    <input
                      type="datetime-local"
                      id="event_date"
                      name="event_date"
                      value={currentEvent.event_date || ''}
                      onChange={(e) => setCurrentEvent({...currentEvent, event_date: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={currentEvent.description || ''}
                    onChange={(e) => setCurrentEvent({...currentEvent, description: e.target.value})}
                    rows="3"
                  ></textarea>
                </div>
                <div className="form-group">
                  <label htmlFor="stakeholders">Stakeholder Groups</label>
                  <Select
                    isMulti
                    id="stakeholders"
                    name="stakeholders"
                    options={groups}
                    value={selectedStakeholders}
                    onChange={handleStakeholderChange}
                    placeholder="Select stakeholder groups..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="key_messages">Key Messages</label>
                  <Select
                    isMulti
                    id="key_messages"
                    name="key_messages"
                    options={keyMessages}
                    value={selectedKeyMessages}
                    onChange={handleKeyMessageChange}
                    placeholder="Select key messages..."
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="tone">Preferred Tone</label>
                    <select
                      id="tone"
                      name="tone"
                      value={currentEvent.tone || 'Informative'}
                      onChange={(e) => setCurrentEvent({...currentEvent, tone: e.target.value})}
                    >
                      <option value="Reassuring">Reassuring</option>
                      <option value="Urgent">Urgent</option>
                      <option value="Inspirational">Inspirational</option>
                      <option value="Executive">Executive</option>
                      <option value="Informative">Informative</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="channels">Communication Channels (comma-separated)</label>
                  <input
                    type="text"
                    id="channels"
                    name="channels"
                    value={Array.isArray(currentEvent.channels) ? currentEvent.channels.join(', ') : currentEvent.channels || ''}
                    onChange={(e) => setCurrentEvent({...currentEvent, channels: e.target.value})}
                  />
                </div>
              </div>

              {/* Communication Content Tab */}
              <div className={`tab-content ${activeTab === 'communication' ? 'active' : ''}`}>
                {currentEvent.id && (
                  <div className="ai-generation-section">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <div style={{ fontWeight: '500' }}>AI-Powered Content Generation</div>
                      <button 
                        type="button" 
                        className="ai-generate-button"
                        onClick={() => handleGenerateCommunication(currentEvent, communicationMedium)}
                        disabled={isGeneratingCommunication}
                        style={{ 
                          fontSize: '0.9rem', 
                          padding: '8px 16px', 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          border: 'none',
                          borderRadius: '8px',
                          color: 'white',
                          fontWeight: '500',
                          boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                        }}
                      >
                        <FiZap /> {isGeneratingCommunication ? 'Generating...' : 'Generate AI Content'}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Communication Medium Selector */}
                <div style={{ marginBottom: '12px' }}>
                  <label htmlFor="communication_medium" style={{ fontSize: '0.9rem', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Communication Type</label>
                  <select
                    id="communication_medium"
                    value={communicationMedium}
                    onChange={(e) => setCommunicationMedium(e.target.value)}
                    style={{ width: '250px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  >
                    <option value="email">📧 Email Communication</option>
                    <option value="presentation">📊 Presentation Materials</option>
                    <option value="workshop">🎯 Workshop Content</option>
                    <option value="meeting">🤝 Meeting Materials</option>
                    <option value="announcement">📢 Announcement</option>
                  </select>
                </div>
                
                {/* Communication Status Indicator */}
                {communicationMedium && currentEvent.structured_communications?.[communicationMedium] && (
                  <div style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span>
                      ✅ AI {communicationMedium} content has been generated and populated below. 
                      <strong> Remember to click "Update Event" to save your changes.</strong>
                    </span>
                  </div>
                )}
                
                {/* Structured Communication Fields based on Medium */}
                {currentEvent ? (
                  <div className="structured-communication-edit">
                    {/* Email Fields */}
                    {communicationMedium === 'email' && (
                      <>
                        <div className="form-group">
                          <label htmlFor="email_subject">Email Subject</label>
                          <input
                            type="text"
                            id="email_subject"
                            value={currentEvent.structured_communications?.email?.subject || ''}
                            onChange={(e) => handleStructuredCommunicationChange('email', 'subject', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="email_opening">Opening</label>
                          <textarea
                            id="email_opening"
                            value={currentEvent.structured_communications?.email?.opening || ''}
                            onChange={(e) => handleStructuredCommunicationChange('email', 'opening', e.target.value)}
                            rows="2"
                          ></textarea>
                        </div>
                        <div className="form-group">
                          <label htmlFor="email_body">Email Body</label>
                          <textarea
                            id="email_body"
                            value={currentEvent.structured_communications?.email?.body || ''}
                            onChange={(e) => handleStructuredCommunicationChange('email', 'body', e.target.value)}
                            rows="6"
                          ></textarea>
                        </div>
                        <div className="form-group">
                          <label htmlFor="email_call_to_action">Call to Action</label>
                          <textarea
                            id="email_call_to_action"
                            value={currentEvent.structured_communications?.email?.call_to_action || ''}
                            onChange={(e) => handleStructuredCommunicationChange('email', 'call_to_action', e.target.value)}
                            rows="2"
                          ></textarea>
                        </div>
                        <div className="form-group">
                          <label htmlFor="email_closing">Closing</label>
                          <textarea
                            id="email_closing"
                            value={currentEvent.structured_communications?.email?.closing || ''}
                            onChange={(e) => handleStructuredCommunicationChange('email', 'closing', e.target.value)}
                            rows="2"
                          ></textarea>
                        </div>
                      </>
                    )}
                    
                    {/* Presentation Fields */}
                    {communicationMedium === 'presentation' && (
                      <>
                        <div className="form-group">
                          <label htmlFor="presentation_title">Presentation Title</label>
                          <input
                            type="text"
                            id="presentation_title"
                            value={currentEvent.structured_communications?.presentation?.title || ''}
                            onChange={(e) => handleStructuredCommunicationChange('presentation', 'title', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="presentation_executive_summary">Executive Summary</label>
                          <textarea
                            id="presentation_executive_summary"
                            value={currentEvent.structured_communications?.presentation?.executive_summary || ''}
                            onChange={(e) => handleStructuredCommunicationChange('presentation', 'executive_summary', e.target.value)}
                            rows="3"
                          ></textarea>
                        </div>
                        <div className="form-group">
                          <label>Key Points</label>
                          <div className="array-field">
                            {Array.isArray(currentEvent.structured_communications?.presentation?.key_points) && 
                              currentEvent.structured_communications.presentation.key_points.map((point, index) => (
                                <div key={index} className="array-item">
                                  <textarea
                                    value={point}
                                    onChange={(e) => updateArrayItem('presentation', 'key_points', index, e.target.value)}
                                    rows="2"
                                  ></textarea>
                                  <button 
                                    type="button" 
                                    className="remove-item-button"
                                    onClick={() => removeArrayItem('presentation', 'key_points', index)}
                                  >
                                    &times;
                                  </button>
                                </div>
                              ))
                            }
                            <button 
                              type="button" 
                              className="add-item-button"
                              onClick={() => addArrayItem('presentation', 'key_points')}
                            >
                              + Add Key Point
                            </button>
                          </div>
                        </div>
                        <div className="form-group">
                          <label htmlFor="presentation_speaker_notes">Speaker Notes</label>
                          <textarea
                            id="presentation_speaker_notes"
                            value={currentEvent.structured_communications?.presentation?.speaker_notes || ''}
                            onChange={(e) => handleStructuredCommunicationChange('presentation', 'speaker_notes', e.target.value)}
                            rows="4"
                          ></textarea>
                        </div>
                        <div className="form-group">
                          <label>Q&A Preparation</label>
                          <div className="array-field">
                            {Array.isArray(currentEvent.structured_communications?.presentation?.qa_prep) && 
                              currentEvent.structured_communications.presentation.qa_prep.map((qa, index) => (
                                <div key={index} className="array-item">
                                  <textarea
                                    value={qa}
                                    onChange={(e) => updateArrayItem('presentation', 'qa_prep', index, e.target.value)}
                                    rows="3"
                                  ></textarea>
                                  <button 
                                    type="button" 
                                    className="remove-item-button"
                                    onClick={() => removeArrayItem('presentation', 'qa_prep', index)}
                                  >
                                    &times;
                                  </button>
                                </div>
                              ))
                            }
                            <button 
                              type="button" 
                              className="add-item-button"
                              onClick={() => addArrayItem('presentation', 'qa_prep')}
                            >
                              + Add Q&A Item
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Workshop Fields */}
                    {communicationMedium === 'workshop' && (
                      <>
                        <div className="form-group">
                          <label>Agenda</label>
                          <div className="array-field">
                            {Array.isArray(currentEvent.structured_communications?.workshop?.agenda) && 
                              currentEvent.structured_communications.workshop.agenda.map((item, index) => (
                                <div key={index} className="array-item">
                                  <textarea
                                    value={item}
                                    onChange={(e) => updateArrayItem('workshop', 'agenda', index, e.target.value)}
                                    rows="2"
                                  ></textarea>
                                  <button 
                                    type="button" 
                                    className="remove-item-button"
                                    onClick={() => removeArrayItem('workshop', 'agenda', index)}
                                  >
                                    &times;
                                  </button>
                                </div>
                              ))
                            }
                            <button 
                              type="button" 
                              className="add-item-button"
                              onClick={() => addArrayItem('workshop', 'agenda')}
                            >
                              + Add Agenda Item
                            </button>
                          </div>
                        </div>
                        <div className="form-group">
                          <label htmlFor="workshop_facilitator_guide">Facilitator Guide</label>
                          <textarea
                            id="workshop_facilitator_guide"
                            value={currentEvent.structured_communications?.workshop?.facilitator_guide || ''}
                            onChange={(e) => handleStructuredCommunicationChange('workshop', 'facilitator_guide', e.target.value)}
                            rows="4"
                          ></textarea>
                        </div>
                        <div className="form-group">
                          <label htmlFor="workshop_participant_brief">Participant Brief</label>
                          <textarea
                            id="workshop_participant_brief"
                            value={currentEvent.structured_communications?.workshop?.participant_brief || ''}
                            onChange={(e) => handleStructuredCommunicationChange('workshop', 'participant_brief', e.target.value)}
                            rows="3"
                          ></textarea>
                        </div>
                        <div className="form-group">
                          <label>Activities</label>
                          <div className="array-field">
                            {Array.isArray(currentEvent.structured_communications?.workshop?.activities) && 
                              currentEvent.structured_communications.workshop.activities.map((activity, index) => (
                                <div key={index} className="array-item">
                                  <textarea
                                    value={activity}
                                    onChange={(e) => updateArrayItem('workshop', 'activities', index, e.target.value)}
                                    rows="3"
                                  ></textarea>
                                  <button 
                                    type="button" 
                                    className="remove-item-button"
                                    onClick={() => removeArrayItem('workshop', 'activities', index)}
                                  >
                                    &times;
                                  </button>
                                </div>
                              ))
                            }
                            <button 
                              type="button" 
                              className="add-item-button"
                              onClick={() => addArrayItem('workshop', 'activities')}
                            >
                              + Add Activity
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Meeting Fields */}
                    {communicationMedium === 'meeting' && (
                      <>
                        <div className="form-group">
                          <label>Agenda</label>
                          <div className="array-field">
                            {(() => {
                              const agendaItems = currentEvent.structured_communications?.meeting?.agenda;
                              console.log('🎯 Agenda items for display:', agendaItems);
                              console.log('🎯 Is array?', Array.isArray(agendaItems));
                              console.log('🎯 Length:', agendaItems?.length);
                              
                              if (Array.isArray(agendaItems) && agendaItems.length > 0) {
                                return agendaItems.map((item, index) => {
                                  // Convert object items to string for display in textarea
                                  let displayValue = item;
                                  if (typeof item === 'object' && item !== null) {
                                    if (item.timing && item.item) {
                                      displayValue = `${item.timing} - ${item.item}`;
                                    } else if (item.time && item.item) {
                                      displayValue = `${item.time} - ${item.item}`;
                                    } else if (item.timing && item.topic) {
                                      displayValue = `${item.timing} - ${item.topic}`;
                                    } else if (item.time && item.topic) {
                                      displayValue = `${item.time} - ${item.topic}`;
                                    } else {
                                      // Handle other object structures - avoid showing field names
                                      const entries = Object.entries(item);
                                      if (entries.length === 2) {
                                        const [, value1] = entries[0];
                                        const [, value2] = entries[1];
                                        displayValue = `${value1} - ${value2}`;
                                      } else {
                                        displayValue = Object.values(item).join(' - ');
                                      }
                                    }
                                  }
                                  
                                  return (
                                    <div key={index} className="array-item">
                                      <textarea
                                        value={displayValue}
                                        onChange={(e) => updateArrayItem('meeting', 'agenda', index, e.target.value)}
                                        rows="2"
                                      ></textarea>
                                      <button 
                                        type="button" 
                                        className="remove-item-button"
                                        onClick={() => removeArrayItem('meeting', 'agenda', index)}
                                      >
                                        &times;
                                      </button>
                                    </div>
                                  );
                                });
                              }
                              return null;
                            })()}
                            <button 
                              type="button" 
                              className="add-item-button"
                              onClick={() => addArrayItem('meeting', 'agenda')}
                            >
                              + Add Agenda Item
                            </button>
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Talking Points</label>
                          <div className="array-field">
                            {(() => {
                              const talkingPoints = currentEvent.structured_communications?.meeting?.talking_points;
                              console.log('🎯 Talking points for display:', talkingPoints);
                              
                              if (Array.isArray(talkingPoints) && talkingPoints.length > 0) {
                                return talkingPoints.map((point, index) => {
                                  // Convert object items to string for display in textarea
                                  let displayValue = point;
                                  if (typeof point === 'object' && point !== null) {
                                    if (point.point) {
                                      displayValue = point.point;
                                    } else if (point.topic) {
                                      displayValue = point.topic;
                                    } else if (point.discussion_point) {
                                      displayValue = point.discussion_point;
                                    } else {
                                      displayValue = Object.entries(point)
                                        .map(([key, value]) => `${key}: ${value}`)
                                        .join(', ');
                                    }
                                  }
                                  
                                  return (
                                    <div key={index} className="array-item">
                                      <textarea
                                        value={displayValue}
                                        onChange={(e) => updateArrayItem('meeting', 'talking_points', index, e.target.value)}
                                        rows="2"
                                      ></textarea>
                                      <button 
                                        type="button" 
                                        className="remove-item-button"
                                        onClick={() => removeArrayItem('meeting', 'talking_points', index)}
                                      >
                                        &times;
                                      </button>
                                    </div>
                                  );
                                });
                              }
                              return null;
                            })()}
                            <button 
                              type="button" 
                              className="add-item-button"
                              onClick={() => addArrayItem('meeting', 'talking_points')}
                            >
                              + Add Talking Point
                            </button>
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Decision Points</label>
                          <div className="array-field">
                            {(() => {
                              const decisionPoints = currentEvent.structured_communications?.meeting?.decision_points;
                              console.log('🎯 Decision points for display:', decisionPoints);
                              
                              if (Array.isArray(decisionPoints) && decisionPoints.length > 0) {
                                return decisionPoints.map((point, index) => {
                                  // Convert object items to string for display in textarea
                                  let displayValue = point;
                                  if (typeof point === 'object' && point !== null) {
                                    if (point.decision) {
                                      displayValue = point.decision;
                                    } else if (point.point) {
                                      displayValue = point.point;
                                    } else if (point.decision_point) {
                                      displayValue = point.decision_point;
                                    } else {
                                      displayValue = Object.entries(point)
                                        .map(([key, value]) => `${key}: ${value}`)
                                        .join(', ');
                                    }
                                  }
                                  
                                  return (
                                    <div key={index} className="array-item">
                                      <textarea
                                        value={displayValue}
                                        onChange={(e) => updateArrayItem('meeting', 'decision_points', index, e.target.value)}
                                        rows="2"
                                      ></textarea>
                                      <button 
                                        type="button" 
                                        className="remove-item-button"
                                        onClick={() => removeArrayItem('meeting', 'decision_points', index)}
                                      >
                                        &times;
                                      </button>
                                    </div>
                                  );
                                });
                              }
                              return null;
                            })()}
                            <button 
                              type="button" 
                              className="add-item-button"
                              onClick={() => addArrayItem('meeting', 'decision_points')}
                            >
                              + Add Decision Point
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Announcement Fields */}
                    {communicationMedium === 'announcement' && (
                      <>
                        <div className="form-group">
                          <label htmlFor="announcement_headline">Headline</label>
                          <input
                            type="text"
                            id="announcement_headline"
                            value={currentEvent.structured_communications?.announcement?.headline || ''}
                            onChange={(e) => handleStructuredCommunicationChange('announcement', 'headline', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="announcement_summary">Summary</label>
                          <textarea
                            id="announcement_summary"
                            value={currentEvent.structured_communications?.announcement?.summary || ''}
                            onChange={(e) => handleStructuredCommunicationChange('announcement', 'summary', e.target.value)}
                            rows="2"
                          ></textarea>
                        </div>
                        <div className="form-group">
                          <label htmlFor="announcement_details">Details</label>
                          <textarea
                            id="announcement_details"
                            value={currentEvent.structured_communications?.announcement?.details || ''}
                            onChange={(e) => handleStructuredCommunicationChange('announcement', 'details', e.target.value)}
                            rows="5"
                          ></textarea>
                        </div>
                        <div className="form-group">
                          <label htmlFor="announcement_next_steps">Next Steps</label>
                          <textarea
                            id="announcement_next_steps"
                            value={currentEvent.structured_communications?.announcement?.next_steps || ''}
                            onChange={(e) => handleStructuredCommunicationChange('announcement', 'next_steps', e.target.value)}
                            rows="3"
                          ></textarea>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="placeholder-message">
                    Please save the event details first to enable communication content editing.
                  </div>
                )}
              </div>

              {/* Documents Tab */}
              <div className={`tab-content ${activeTab === 'documents' ? 'active' : ''}`}>
                {currentEvent.id ? (
                  <>
                    <div className="documents-section">
                      <h3 className="section-title">Existing Documents</h3>
                      {eventFiles.length > 0 ? (
                        <div className="documents-container">
                          {eventFiles.map(doc => (
                            <div key={doc.id} className="document-card">
                              <div className="document-icon">
                                <span className="file-type-badge">{doc.file_type.toUpperCase()}</span>
                              </div>
                              <div className="document-info">
                                <h4 className="document-name">{doc.name}</h4>
                                <div className="document-meta">
                                  <span className="document-type">{doc.file_type.toUpperCase()} File</span>
                                </div>
                              </div>
                              <div className="document-actions">
                                <button 
                                  type="button"
                                  className="document-action-btn view-btn"
                                  onClick={async () => {
                                    try {
                                      const fileExtension = doc.file_path.split('.').pop().toLowerCase();
                                      const isViewableInBrowser = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'txt'].includes(fileExtension);
                                      
                                      if (isViewableInBrowser) {
                                        // For PDFs and images, open in browser tab
                                        const response = await apiClient.get(`/uploads/${doc.file_path}`, {
                                          responseType: 'blob'
                                        });
                                        
                                        // Determine MIME type based on extension
                                        let mimeType = 'application/octet-stream';
                                        if (fileExtension === 'pdf') mimeType = 'application/pdf';
                                        else if (['jpg', 'jpeg'].includes(fileExtension)) mimeType = 'image/jpeg';
                                        else if (fileExtension === 'png') mimeType = 'image/png';
                                        else if (fileExtension === 'gif') mimeType = 'image/gif';
                                        else if (fileExtension === 'txt') mimeType = 'text/plain';
                                        
                                        const blob = new Blob([response.data], { type: mimeType });
                                        const url = window.URL.createObjectURL(blob);
                                        window.open(url, '_blank');
                                        
                                        // Clean up blob URL after a delay
                                        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                                      } else {
                                        // For Office docs (Word, Excel), force download
                                        const response = await apiClient.get(`/uploads/${doc.file_path}`, {
                                          responseType: 'blob'
                                        });
                                        
                                        // Create download link
                                        const blob = new Blob([response.data]);
                                        const url = window.URL.createObjectURL(blob);
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.download = doc.name; // Use original filename
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        
                                        // Clean up blob URL
                                        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                                      }
                                    } catch (error) {
                                      console.error('Error viewing/downloading document:', error);
                                      setError('Failed to open document. Please try again.');
                                    }
                                  }}
                                  title={`${['pdf', 'jpg', 'jpeg', 'png', 'gif', 'txt'].includes(doc.file_path.split('.').pop().toLowerCase()) ? 'View' : 'Download'} Document`}
                                >
                                  <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {['pdf', 'jpg', 'jpeg', 'png', 'gif', 'txt'].includes(doc.file_path.split('.').pop().toLowerCase()) ? (
                                      // Eye icon for viewable files
                                      <>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </>
                                    ) : (
                                      // Download icon for office docs
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    )}
                                  </svg>
                                  {['pdf', 'jpg', 'jpeg', 'png', 'gif', 'txt'].includes(doc.file_path.split('.').pop().toLowerCase()) ? 'View' : 'Download'}
                                </button>
                                <button 
                                  type="button" 
                                  className="document-action-btn delete-btn"
                                  onClick={() => handleDeleteEventDocument(currentEvent.id, doc.id)}
                                  title="Delete Document"
                                >
                                  <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-documents-card">
                          <div className="no-documents-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <h4>No Documents</h4>
                          <p>No documents have been attached to this event yet.</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="upload-section">
                      <h3 className="section-title">Upload New Documents</h3>
                      <div 
                        className="dropzone"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleFileDrop}
                      >
                        <div className="dropzone-content">
                          <div className="dropzone-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                          </div>
                          <p className="dropzone-text">Drag and drop files here, or click to select files</p>
                          <input 
                            type="file" 
                            id="file-upload" 
                            multiple 
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                          />
                          <button 
                            type="button" 
                            className="select-files-button"
                            onClick={() => document.getElementById('file-upload').click()}
                          >
                            <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Select Files
                          </button>
                        </div>
                      </div>
                      
                      {fileError && (
                        <div className="file-error">
                          <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {fileError}
                        </div>
                      )}
                      
                      {selectedFiles.length > 0 && (
                        <div className="selected-files-section">
                          <h4 className="selected-files-title">Selected Files</h4>
                          <div className="selected-files-list">
                            {selectedFiles.map((file, index) => (
                              <div key={index} className="selected-file-item">
                                <div className="file-info">
                                  <div className="file-name">{file.name}</div>
                                  <div className="file-size">{(file.size / 1024).toFixed(1)} KB</div>
                                </div>
                                <button 
                                  type="button" 
                                  className="file-remove-btn"
                                  onClick={() => removeSelectedFile(index)}
                                  title="Remove file"
                                >
                                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                                {uploadProgress[index] !== undefined && (
                                  <div className="upload-progress-bar">
                                    <div 
                                      className="progress-fill" 
                                      style={{ width: `${uploadProgress[index]}%` }}
                                    ></div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {uploadStatus && (
                        <div className={`upload-status ${uploadStatus.includes('success') ? 'success' : uploadStatus.includes('Error') ? 'error' : 'info'}`}>
                          <svg className="status-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {uploadStatus}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="placeholder-message">
                    <div className="placeholder-icon">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h4>Save Event First</h4>
                    <p>Please save the event details first to enable document uploads.</p>
                  </div>
                )}
              </div>
              </div>{/* End of tab-content-container */}
            </div>
            <div className="modal-footer">
              <button type="button" className="button secondary" onClick={() => {
                console.log('🚫 Event modal Cancel button clicked, closing modal');
                setIsEventModalOpen(false);
              }}>Cancel</button>
              <button type="submit" className="button primary" disabled={loading}>
                {loading ? 'Saving...' : currentEvent.id ? 'Update Event' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Helper function to convert complex objects to readable text (similar to backend)
  const convertToReadableText = (obj, indentLevel = 0) => {
    const indent = "  ".repeat(indentLevel);
    
    if (typeof obj === 'string') return obj;
    
    if (obj === null || obj === undefined) return '';
    
    if (Array.isArray(obj)) {
      if (obj.length === 0) return '';
      
      return obj.map((item, index) => {
        if (typeof item === 'object' && item !== null) {
          const nested = convertToReadableText(item, indentLevel + 1);
          return nested ? `${indent}${index + 1}. ${nested}` : '';
        }
        return `${indent}• ${item}`;
      }).filter(Boolean).join('\n');
    }
    
    if (typeof obj === 'object') {
      const result = [];
      
      for (const [key, value] of Object.entries(obj)) {
        if (!value) continue;
        
        const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        
        if (typeof value === 'object') {
          const nestedContent = convertToReadableText(value, indentLevel + 1);
          if (nestedContent) {
            result.push(`${indent}${formattedKey}:\n${nestedContent}`);
          }
        } else {
          result.push(`${indent}${formattedKey}: ${value}`);
        }
      }
      
      return result.join('\n');
    }
    
    return String(obj);
  };

  const renderDocumentModal = () => {
    if (!showDocumentModal) return null;
    
    return (
      <div className="modal show">
        <div className="modal-backdrop" onClick={() => setShowDocumentModal(false)}></div>
        <div className="modal-content large-modal">
          <div className="modal-header">
            <h2>Generated Documents</h2>
            <button className="close-button" onClick={() => setShowDocumentModal(false)}>&times;</button>
          </div>
          <div className="modal-body">
            {generatedDocuments.length === 0 ? (
              <div className="no-documents">
                <p>No documents have been generated yet.</p>
                <div className="document-actions">
                  <button 
                    className="generate-button" 
                    onClick={() => {
                      setShowDocumentModal(false);
                      handleGenerateDocument('change_management_plan');
                    }}
                  >
                    <FiFileText className="icon" />
                    Generate Change Management Plan
                  </button>
                  <button 
                    className="generate-button" 
                    onClick={() => {
                      setShowDocumentModal(false);
                      handleGenerateDocument('communication_plan');
                    }}
                  >
                    <FiFileText className="icon" />
                    Generate Communication Plan
                  </button>
                </div>
              </div>
            ) : (
              <div className="documents-list">
                <table className="documents-table">
                  <thead>
                    <tr>
                      <th>Document Name</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generatedDocuments.map(doc => (
                      <tr key={doc.id}>
                        <td>{doc.document_name}</td>
                        <td>{doc.document_type === 'change_management_plan' ? 'Change Management Plan' : 'Communication Plan'}</td>
                        <td><span className={`status-badge ${doc.status}`}>{doc.status}</span></td>
                        <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="document-actions">
                            <button 
                              className="download-button"
                              onClick={() => handleDownloadDocument(doc.id)}
                              title="Download as Word document"
                            >
                              <FiDownload className="icon" />
                              Download
                            </button>
                            <button 
                              className="rename-button"
                              onClick={() => handleRenameDocument(doc.id, doc.document_name)}
                              title="Rename document"
                            >
                              <FiEdit className="icon" />
                              Rename
                            </button>
                            <button 
                              className="delete-button"
                              onClick={() => handleDeleteDocument(doc.id)}
                              title="Delete document"
                            >
                              <FiTrash className="icon" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="button secondary" onClick={() => setShowDocumentModal(false)}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCommunicationModal = () => {
    if (!showCommunicationModal) return null;

    console.log('🎭 renderCommunicationModal Debug:', {
      showCommunicationModal,
      generatedCommunication,
      communicationMedium,
      hasCurrentEventStructured: !!currentEvent?.structured_communications,
      structuredForMedium: currentEvent?.structured_communications?.[communicationMedium]
    });

    const renderFormattedContent = () => {
      // Priority 1: Use generatedCommunication if available (this is the fresh AI response)
      let communicationData = generatedCommunication;
      
      // Priority 2: Fall back to currentEvent structured communications
      if (!communicationData && currentEvent?.structured_communications?.[communicationMedium]) {
        communicationData = currentEvent.structured_communications[communicationMedium];
      }
      
      console.log('🎯 Final communication data for display:', communicationData);
      console.log('🎯 Data type:', typeof communicationData);
      console.log('🎯 Is Array:', Array.isArray(communicationData));
      console.log('🎯 Object keys:', communicationData ? Object.keys(communicationData) : 'null');

      if (!communicationData) {
        return (
          <div className="no-content-message">
            <h4>No Communication Content Available</h4>
            <p>No content was generated or found for the {communicationMedium} medium.</p>
          </div>
        );
      }

      // Render based on communication medium
      const mediumIcons = {
        email: '📧',
        presentation: '📊', 
        workshop: '🎯',
        meeting: '🤝',
        announcement: '📢'
      };

      const mediumTitles = {
        email: 'Email Communication',
        presentation: 'Presentation Materials',
        workshop: 'Workshop Materials', 
        meeting: 'Meeting Materials',
        announcement: 'Announcement'
      };

      // Helper function to render agenda items with proper formatting
      const renderAgendaItems = (agenda) => {
        if (!agenda || !Array.isArray(agenda) || agenda.length === 0) return null;
        
        return (
          <div className="communication-section">
            <h4>📅 Meeting Agenda</h4>
            <div className="communication-content">
              <div className="agenda-items">
                {agenda.map((item, index) => {
                  let displayText = '';
                  
                  if (typeof item === 'object' && item !== null) {
                    // Handle structured agenda items
                    if (item.time && item.item) {
                      displayText = `${item.time} - ${item.item}`;
                    } else if (item.timing && item.topic) {
                      displayText = `${item.timing} - ${item.topic}`;
                    } else if (item.time && item.topic) {
                      displayText = `${item.time} - ${item.topic}`;
                    } else {
                      // Fallback for other object structures
                      displayText = Object.entries(item)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(', ');
                    }
                  } else {
                    displayText = String(item);
                  }
                  
                  return (
                    <div key={index} className="agenda-item">
                      <span className="agenda-number">{index + 1}.</span>
                      <span className="agenda-text">{displayText}</span>
                      </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      };

      // Helper function to render talking points with proper formatting
      const renderTalkingPoints = (talkingPoints) => {
        if (!talkingPoints || !Array.isArray(talkingPoints) || talkingPoints.length === 0) return null;
        
        return (
          <div className="communication-section">
            <h4>💬 Key Discussion Points</h4>
            <div className="communication-content">
              <div className="talking-points">
                {talkingPoints.map((point, index) => {
                  let displayText = '';
                  
                  if (typeof point === 'object' && point !== null) {
                    // Handle structured talking points
                    if (point.point) {
                      displayText = point.point;
                    } else if (point.topic) {
                      displayText = point.topic;
                    } else if (point.discussion_point) {
                      displayText = point.discussion_point;
                    } else {
                      // Fallback for other object structures
                      displayText = Object.entries(point)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(', ');
                    }
                  } else {
                    displayText = String(point);
                  }
                  
                  return (
                    <div key={index} className="talking-point">
                      <span className="point-bullet">•</span>
                      <span className="point-text">{displayText}</span>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      };

      // Helper function to render decision points with proper formatting
      const renderDecisionPoints = (decisionPoints) => {
        if (!decisionPoints || !Array.isArray(decisionPoints) || decisionPoints.length === 0) return null;
        
        return (
          <div className="communication-section">
            <h4>⚡ Decisions Required</h4>
            <div className="communication-content">
              <div className="decision-points">
                {decisionPoints.map((decision, index) => {
                  let displayText = '';
                  
                  if (typeof decision === 'object' && decision !== null) {
                    // Handle structured decision points
                    if (decision.decision) {
                      displayText = decision.decision;
                    } else if (decision.point) {
                      displayText = decision.point;
                    } else if (decision.decision_point) {
                      displayText = decision.decision_point;
                    } else {
                      // Fallback for other object structures
                      displayText = Object.entries(decision)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(', ');
                    }
                  } else {
                    displayText = String(decision);
                  }
                  
                  return (
                    <div key={index} className="decision-point">
                      <span className="decision-number">#{index + 1}</span>
                      <span className="decision-text">{displayText}</span>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      };

      // Helper function to render any other content with improved formatting
      const renderOtherContent = (content, title) => {
        if (!content) return null;
        
        // Convert complex objects to readable text
        let displayContent = content;
        if (typeof content === 'object' && content !== null) {
          displayContent = convertToReadableText(content);
        }
        
        return (
          <div className="communication-section">
            <h4>{title}</h4>
            <div className="communication-content">
              <div className="communication-text">
                <pre style={{
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  margin: 0,
                  backgroundColor: 'transparent'
                }}>
                  {displayContent}
                </pre>
              </div>
            </div>
          </div>
        );
      };

      return (
        <div className="formatted-communication">
          <div className="communication-header">
            <h3>{mediumIcons[communicationMedium] || '📄'} {mediumTitles[communicationMedium] || 'Communication'}</h3>
          </div>
          
          {/* Render content based on communication medium */}
          {communicationMedium === 'meeting' && typeof communicationData === 'object' ? (
            <>
              {/* Render agenda items with special formatting */}
              {communicationData.agenda && renderAgendaItems(communicationData.agenda)}
              
              {/* Render talking points */}
              {communicationData.talking_points && renderTalkingPoints(communicationData.talking_points)}
              
              {/* Render decision points */}
              {communicationData.decision_points && renderDecisionPoints(communicationData.decision_points)}
              
              {/* Render any other fields */}
              {Object.entries(communicationData).map(([key, value]) => {
                if (['agenda', 'talking_points', 'decision_points'].includes(key)) {
                  return null; // Already rendered above
                }
                const displayTitle = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                return renderOtherContent(value, displayTitle);
              }).filter(Boolean)}
            </>
          ) : (
            /* Render for other communication mediums */
            Object.entries(communicationData).map(([key, value]) => {
              const displayTitle = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              return renderOtherContent(value, displayTitle);
            }).filter(Boolean)
          )}
          
          {/* Fallback if no content was rendered */}
          {(!communicationData || (typeof communicationData === 'object' && Object.keys(communicationData).length === 0)) && (
            <div className="no-content-message">
              <p>The communication was generated but appears to be empty.</p>
              <details>
                <summary>Raw Data (for debugging)</summary>
                <pre>{JSON.stringify(communicationData, null, 2)}</pre>
              </details>
            </div>
          )}
        </div>
      );
    };

    const handleCloseModal = () => {
      console.log('🚫 Closing communication modal');
      setShowCommunicationModal(false);
    };

    const handleCloseAndUpdate = async () => {
      console.log('💾 Close and update event clicked');
      
      // CRITICAL: Ensure the generated content is properly saved to currentEvent
      if (generatedCommunication && communicationMedium) {
        console.log('📝 Updating currentEvent with generated communication:', {
          medium: communicationMedium,
          data: generatedCommunication
        });
        
        try {
          setIsGeneratingCommunication(true); // Show loading state
        
        // Convert AI-generated structured data to form-compatible format
        const formatDataForForm = (data) => {
          console.log('🔧 formatDataForForm - Input data:', data);
          const formattedData = { ...data };
          
          // Handle meeting data conversion
          if (communicationMedium === 'meeting' && data) {
            // Check if data has meeting_materials (AI response format)
            if (data.meeting_materials) {
              console.log('📋 Found meeting_materials:', data.meeting_materials);
              formattedData.agenda = data.meeting_materials.agenda || [];
              formattedData.talking_points = data.meeting_materials.talking_points || [];
              formattedData.decision_points = data.meeting_materials.decision_points || [];
            } else {
              // Fallback to direct properties
              formattedData.agenda = data.agenda || [];
              formattedData.talking_points = data.talking_points || [];
              formattedData.decision_points = data.decision_points || [];
            }
            
            console.log('📋 After extraction - agenda:', formattedData.agenda);
            console.log('📋 After extraction - talking_points:', formattedData.talking_points);
            console.log('📋 After extraction - decision_points:', formattedData.decision_points);
            
            // Convert agenda items from objects to strings
            if (Array.isArray(formattedData.agenda)) {
              formattedData.agenda = formattedData.agenda.map(item => {
                if (typeof item === 'object' && item !== null) {
                  // Handle "timing" and "item" properties (new format)
                  if (item.timing && item.item) {
                    return `${item.timing} - ${item.item}`;
                  }
                  // Handle "time" and "item" properties
                  if (item.time && item.item) {
                    return `${item.time} - ${item.item}`;
                  }
                  // Handle "timing" and "topic" properties
                  if (item.timing && item.topic) {
                    return `${item.timing} - ${item.topic}`;
                  }
                  // Handle "time" and "topic" properties
                  if (item.time && item.topic) {
                    return `${item.time} - ${item.topic}`;
                  }
                  // Handle other object structures - but avoid showing field names like "item:"
                  const entries = Object.entries(item);
                  if (entries.length === 2) {
                    // If it's a simple 2-field object, format as "value1 - value2"
                    const [, value1] = entries[0];
                    const [, value2] = entries[1];
                    return `${value1} - ${value2}`;
                  }
                  // Fallback for complex objects
                  return Object.values(item).join(' - ');
                }
                return String(item);
              });
            }
            
            // Convert talking points from objects to strings
            if (Array.isArray(formattedData.talking_points)) {
              formattedData.talking_points = formattedData.talking_points.map(item => {
                if (typeof item === 'object' && item !== null) {
                  if (item.point || item.topic) {
                    return item.point || item.topic;
                  }
                  return Object.entries(item).map(([key, value]) => `${key}: ${value}`).join(', ');
                }
                return String(item);
              });
            }
            
            // Convert decision points from objects to strings
            if (Array.isArray(formattedData.decision_points)) {
              formattedData.decision_points = formattedData.decision_points.map(item => {
                if (typeof item === 'object' && item !== null) {
                  if (item.decision || item.point) {
                    return item.decision || item.point;
                  }
                  return Object.entries(item).map(([key, value]) => `${key}: ${value}`).join(', ');
                }
                return String(item);
              });
            }
          }
          
          // Handle announcement data conversion
          if (communicationMedium === 'announcement' && data) {
            // Convert complex objects to readable text for announcement fields
            if (formattedData.details && typeof formattedData.details === 'object') {
              formattedData.details = convertToReadableText(formattedData.details);
            }
            if (formattedData.next_steps && typeof formattedData.next_steps === 'object') {
              formattedData.next_steps = convertToReadableText(formattedData.next_steps);
            }
          }
          
          // Handle other mediums (email, presentation, workshop)
          // Convert any arrays of objects to arrays of strings
          Object.keys(formattedData).forEach(key => {
            if (Array.isArray(formattedData[key])) {
              formattedData[key] = formattedData[key].map(item => {
                if (typeof item === 'object' && item !== null) {
                  // For key messages, extract the main content
                  if (item.title && item.supporting_statement) {
                    return `${item.title}: ${item.supporting_statement}`;
                  }
                  // For other objects, convert to string representation
                  return Object.entries(item).map(([k, v]) => `${k}: ${v}`).join(', ');
                }
                return String(item);
              });
            }
          });
          
          console.log('🔧 formatDataForForm - Final formatted data:', formattedData);
          return formattedData;
        };
        
        const formattedCommunication = formatDataForForm(generatedCommunication);
        
        console.log('🔄 Formatted communication for form:', formattedCommunication);
        
          // Prepare the event data with structured communications
          const updatedEventData = {
            ...currentEvent,
            structured_communications: {
              ...currentEvent.structured_communications,
              [communicationMedium]: formattedCommunication
            },
            target_medium: communicationMedium,
            generated_communication: typeof generatedCommunication === 'object' 
              ? JSON.stringify(generatedCommunication) 
              : generatedCommunication
          };
          
          console.log('🚀 Saving event with generated communication to backend:', {
            eventId: currentEvent.id,
            medium: communicationMedium,
            hasStructuredComms: !!updatedEventData.structured_communications
          });
          
          // Save the updated event to the backend
          await saveEventToBackend(updatedEventData, currentEvent.id);
          
          console.log('✅ Event successfully updated with generated communication');
          
          // Update the frontend state
        setCurrentEvent(prev => ({
          ...prev,
          structured_communications: {
            ...prev.structured_communications,
            [communicationMedium]: formattedCommunication
          },
          target_medium: communicationMedium,
          generated_communication: typeof generatedCommunication === 'object' 
            ? JSON.stringify(generatedCommunication) 
            : generatedCommunication
        }));
        
          // Refresh the events list to get the latest data from backend
          await fetchEvents();
          
          // IMPORTANT: Refresh the current event data so the modal shows saved content
          await refreshCurrentEventData(currentEvent.id);
          
          // Force modal to re-render by updating a key
          setIsEventModalOpen(false);
          setTimeout(() => {
            setIsEventModalOpen(true);
          }, 100);
          
          setSuccessMessage('✅ Generated communication has been saved to the event successfully!');
          setTimeout(() => setSuccessMessage(null), 5000);
          
        } catch (error) {
          console.error('❌ Error saving generated communication:', error);
          setError(`Error saving generated communication: ${error.message}`);
        } finally {
          setIsGeneratingCommunication(false);
        }
        
        // Close modal after successful save
        setShowCommunicationModal(false);
        
      } else {
        console.warn('⚠️ No communication data to save');
        setShowCommunicationModal(false);
      }
    };

    return (
      <div className={`modal ${showCommunicationModal ? 'show' : ''}`} style={{ display: showCommunicationModal ? 'block' : 'none' }}>
        <div className="modal-backdrop" onClick={handleCloseModal}></div>
        <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Generated Communication - {communicationMedium?.toUpperCase()}</h2>
            <button className="close-button" onClick={handleCloseModal}>&times;</button>
          </div>
          <div className="modal-body">
            {renderFormattedContent()}
          </div>
          <div className="modal-footer">
            <button type="button" className="button secondary" onClick={handleCloseModal}>Close</button>
            <button 
              type="button" 
              className="button primary" 
              onClick={handleCloseAndUpdate}
              disabled={isGeneratingCommunication}
            >
              {isGeneratingCommunication ? (
                <>
                  <span className="loading-spinner-small"></span>
                  Saving...
                </>
              ) : (
                'Close & Update Event'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Debug logging for modal state
  useEffect(() => {
    console.log('📊 Plans Debug - Modal State:', { 
      isModalOpen, 
      currentPlan, 
      timestamp: new Date().toISOString() 
    });
  }, [isModalOpen, currentPlan]);

  // Force close modal function for debugging
  const forceCloseModal = useCallback(() => {
    console.log('🔧 Force closing modal');
    setIsModalOpen(false);
    setCurrentPlan(null);
  }, []);

  // Make force close available globally for debugging
  useEffect(() => {
    window.forceCloseModal = forceCloseModal;
    return () => {
      delete window.forceCloseModal;
    };
  }, [forceCloseModal]);

  // Helper function to refresh current event data from the backend
  const refreshCurrentEventData = async (eventId) => {
    try {
      console.log('🔄 Refreshing current event data for ID:', eventId);
      const response = await apiClient.get(`/events/${eventId}`);
      const updatedEvent = response.data;
      
      console.log('📥 Received updated event data:', updatedEvent);
      
      // Update the currentEvent with fresh data from backend
      if (updatedEvent) {
        // Process the event data similar to handleEditEvent
        const structured_communications = updatedEvent.structured_communications || {};
        const completeStructuredComms = {
          email: structured_communications.email || { subject: '', opening: '', body: '', call_to_action: '', closing: '' },
          presentation: structured_communications.presentation || { title: '', executive_summary: '', key_points: [], speaker_notes: '', qa_prep: [] },
          workshop: structured_communications.workshop || { agenda: [], facilitator_guide: '', participant_brief: '', activities: [] },
          meeting: structured_communications.meeting || { agenda: [], talking_points: [], decision_points: [] },
          announcement: structured_communications.announcement || { headline: '', summary: '', details: '', next_steps: '' }
        };

        // Parse any JSON string fields for arrays and ensure they're properly formatted
        Object.keys(completeStructuredComms).forEach(medium => {
          const mediumData = completeStructuredComms[medium];
          Object.keys(mediumData).forEach(field => {
            // Handle array fields that might be JSON strings
            if (typeof mediumData[field] === 'string' && 
                (field === 'key_points' || field === 'qa_prep' || field === 'agenda' || 
                 field === 'activities' || field === 'talking_points' || field === 'decision_points')) {
              try {
                mediumData[field] = JSON.parse(mediumData[field]);
              } catch (e) {
                // If parsing fails, ensure it's an array
                mediumData[field] = Array.isArray(mediumData[field]) ? mediumData[field] : [];
              }
            }
            
            // Ensure arrays are properly formatted for display
            if (Array.isArray(mediumData[field])) {
              mediumData[field] = mediumData[field].map(item => {
                if (typeof item === 'object' && item !== null) {
                  // Keep structured objects as they are for proper display
                  return item;
                } else {
                  return String(item || '');
                }
              });
            }
          });
        });

        setCurrentEvent(prev => ({
          ...prev,
          id: updatedEvent.id,
          name: updatedEvent.name || prev.name,
          type: updatedEvent.event_type || prev.type,
          event_date: updatedEvent.event_date || prev.event_date,
          description: updatedEvent.description || prev.description,
          structured_communications: completeStructuredComms,
          target_medium: updatedEvent.target_medium || prev.target_medium,
          generated_communication: updatedEvent.generated_communication || prev.generated_communication
        }));
        
        console.log('✅ Current event data refreshed successfully');
      }
    } catch (error) {
      console.error('❌ Error refreshing current event data:', error);
    }
  };

  // Main component render
  return (
    <div className="plans-container">
      <div className="plans-header">
        <h1>Communication Plans</h1>
        <div className="plans-actions">
          <button className="create-button" onClick={handleCreatePlan}>
            <FiPlus className="icon" />
            Create New Plan
          </button>
          <button 
            className="generate-button" 
            onClick={() => handleGenerateDocument('change_management_plan')}
            disabled={!selectedProject || isGeneratingDocument}
            title="Generate a comprehensive Change Management Plan using AI"
          >
            <FiFileText className="icon" />
            {isGeneratingDocument && documentType === 'change_management_plan' ? 'Generating...' : 'Generate CM Plan'}
          </button>
          <button 
            className="generate-button" 
            onClick={() => handleGenerateDocument('communication_plan')}
            disabled={!selectedProject || isGeneratingDocument}
            title="Generate a comprehensive Communication Plan using AI"
          >
            <FiFileText className="icon" />
            {isGeneratingDocument && documentType === 'communication_plan' ? 'Generating...' : 'Generate Comm Plan'}
          </button>
          {generatedDocuments.length > 0 && (
            <button 
              className="view-documents-button" 
              onClick={() => setShowDocumentModal(true)}
              title="View all generated documents"
            >
              <FiDownload className="icon" />
              View Documents ({generatedDocuments.length})
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <FiAlertCircle className="icon" />
          {error}
          <button className="close-button" onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
          <button className="close-button" onClick={() => setSuccessMessage(null)}>&times;</button>
        </div>
      )}

      <div className="project-selector">
        <label htmlFor="project-select">Select Project:</label>
        <select
          id="project-select"
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          disabled={loading || projects.length === 0}
        >
          {projects.map(project => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading">Loading plans...</div>
      ) : plans.length === 0 ? (
        <div className="no-plans">
          <p>No communication plans found for this project.</p>
          <button className="create-button" onClick={handleCreatePlan}>
            <FiPlus className="icon" />
            Create Your First Plan
          </button>
        </div>
      ) : (
        <div className="plans-grid">
          {plans.map(plan => {
            const planEvents = events.filter(event => event.project_id === plan.project_id);
            const areEventsVisible = visibleEvents[plan.id];
            
            return (
              <div key={plan.id} className="plan-with-events">
                {/* Plan Card */}
                <div className="plan-card">
                  <div className="plan-header">
                    <h2>{plan.name}</h2>
                    <div className="plan-badges">
                      <span className={`plan-type ${plan.type}`}>{plan.type}</span>
                      <span className={`plan-status ${plan.status}`}>{plan.status}</span>
                    </div>
                  </div>
                  <div className="plan-dates">
                    <div>
                      <strong>Start:</strong> {new Date(plan.start_date).toLocaleDateString()}
                    </div>
                    <div>
                      <strong>End:</strong> {new Date(plan.end_date).toLocaleDateString()}
                    </div>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                  
                  <div className="plan-channels">
                    <strong>Channels:</strong>
                    <div className="channel-tags">
                      {plan.channels && plan.channels.map((channel, index) => (
                        <span key={index} className="channel-tag">{channel}</span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="plan-actions">
                    <button 
                      className="edit-button" 
                      onClick={() => handleEditPlan(plan)}
                    >
                      <FiEdit2 className="icon" />
                      Edit
                    </button>
                    <button 
                      className="delete-button" 
                      onClick={() => handleDeletePlan(plan.id)}
                    >
                      <FiTrash2 className="icon" />
                      Delete
                    </button>
                    <button 
                      className={`toggle-events-button ${areEventsVisible ? 'active' : ''}`}
                      onClick={() => toggleEventVisibility(plan.id)}
                    >
                      {areEventsVisible ? <FiEyeOff className="icon" /> : <FiEye className="icon" />}
                      {areEventsVisible ? 'Hide Events' : `Show Events (${planEvents.length})`}
                    </button>
                  </div>
                  
                  <div className="plan-summary">
                    <div className="events-summary">
                      <span className="event-count">{planEvents.length}</span>
                      <span className="event-label">Event{planEvents.length !== 1 ? 's' : ''}</span>
                    </div>
                    <button 
                      className="create-event-button" 
                      onClick={handleCreateEvent}
                    >
                      <FiPlus className="icon" />
                      Add Event
                    </button>
                  </div>
                </div>

                {/* Visual Event Connections */}
                {areEventsVisible && planEvents.length > 0 && (
                  <div className="events-visual-container">
                    <div className="events-connector-line"></div>
                    <div className="events-flow">
                      {planEvents
                        .sort((a, b) => {
                          const dateA = new Date(a.event_date);
                          const dateB = new Date(b.event_date);
                          
                          // Sort by Year, then Month, then Day
                          const yearDiff = dateA.getFullYear() - dateB.getFullYear();
                          if (yearDiff !== 0) return yearDiff;
                          
                          const monthDiff = dateA.getMonth() - dateB.getMonth();
                          if (monthDiff !== 0) return monthDiff;
                          
                          return dateA.getDate() - dateB.getDate();
                        })
                        .map((event, index) => (
                          <div key={event.id} className="event-node-container">
                            <div className="event-connector-dot"></div>
                            <div className="event-node" onClick={() => handleEditEvent(event)}>
                              <div className="event-node-header">
                                <div className="event-node-title">{event.name}</div>
                                <div className="event-node-type">{event.event_type}</div>
                              </div>
                              <div className="event-node-date">
                                {new Date(event.event_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                              <div className="event-node-time">
                                {new Date(event.event_date).toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </div>
                              {event.importance_level && (
                                <div className={`event-node-importance ${event.importance_level.toLowerCase()}`}>
                                  {event.importance_level}
                                </div>
                              )}
                              <div className="event-node-actions">
                                <button 
                                  className="event-send-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEventToSend(event);
                                    setShowSendCommunicationModal(true);
                                  }}
                                  title="Send Communications"
                                >
                                  <FiSend />
                                </button>
                                <button 
                                  className="event-edit-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditEvent(event);
                                  }}
                                >
                                  <FiEdit2 />
                                </button>
                                <button 
                                  className="event-delete-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteEvent(event.id);
                                  }}
                                >
                                  <FiTrash2 />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* No Events Message */}
                {areEventsVisible && planEvents.length === 0 && (
                  <div className="no-events-visual">
                    <div className="no-events-connector"></div>
                    <div className="no-events-message">
                      <FiCalendar className="no-events-icon" />
                      <span>No events yet</span>
                      <button 
                        className="create-first-event-btn"
                        onClick={handleCreateEvent}
                      >
                        <FiPlus /> Create First Event
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {renderPlanModal()}
      {renderEventModal()}
      {renderCommunicationModal()}
      {renderDocumentModal()}
      <SendCommunicationModal
        show={showSendCommunicationModal}
        onHide={() => {
          setShowSendCommunicationModal(false);
          setEventToSend(null);
        }}
        event={eventToSend}
        onSuccess={handleSendCommunicationSuccess}
      />
      <DocumentSectionSelector
        show={showSectionSelector}
        onHide={() => {
          setShowSectionSelector(false);
          setPendingDocumentType(null);
        }}
        projectId={selectedProject}
        documentType={pendingDocumentType}
        onGenerate={handleGenerateWithSections}
      />
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        type={confirmationModal.type}
        onConfirm={confirmationModal.onConfirm}
        onCancel={closeConfirmationModal}
        isLoading={confirmationModal.isLoading}
      />
    </div>
  );
};

export default Plans;
